import React, { useMemo, useState, useCallback } from 'react';
import { Select } from '../ui-kit';
import { IconCircleInfo } from '../ui-kit/icons';
import { sortBy, isEmpty } from 'lodash';
import { BarLoader } from 'react-spinners';

import SkillIcon from './SkillIcon';

const filterOption = (op, filterValue) => op.data.textLabel.toLowerCase().includes(filterValue.toLowerCase());

const customStyles = {
  menuList: base => ({
    ...base,
    height: 190
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

const AvailabilityCodeSelector = ({
  multi = false,
  selectedAvailabilityCodes,
  availabilityCodes,
  loading,
  onChange,
  hideExistingIntervalsWarning,
  customInfoComponent,
  inputName
}) => {
  selectedAvailabilityCodes = selectedAvailabilityCodes || [];
  // if it's an array, it stays an array. If it's a hash from ID to object,
  // transform it to an array of objects
  availabilityCodes = sortBy(Object.values(availabilityCodes || []), ['display_order', 'name']);

  const [selectedAvailabilityCodeIds, setSelectedAvailabilityCodeIds] = useState(
    selectedAvailabilityCodes.map(i => i.id)
  );

  // calculate the external onChange listener, if it exists. This can be a
  // string with the name of the method to call, which is why `eval` is used
  // here.
  if (onChange) {
    onChange = typeof onChange === 'string' ? eval(onChange) : onChange;
  }

  const handleChange = events => {
    if (!Array.isArray(events)) events = [events].filter(Boolean);
    const values = events.map(e => e.value);
    setSelectedAvailabilityCodeIds(values);
    if (onChange) onChange(values);
  };

  const generateOptions = useCallback(
    simplified =>
      sortBy(availabilityCodes, ['display_order', 'name']).map(c => {
        const textLabel = c.name || c.short_code;
        const label = (
          <div style={{ maxHeight: '48px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <SkillIcon skill={c} /> <span>{textLabel}</span>
            </div>
            {!simplified && <small>{c.description}</small>}
          </div>
        );
        return { value: c.id, textLabel, label };
      }),
    [availabilityCodes]
  );

  const options = useMemo(generateOptions, [generateOptions]);

  const selectedOptions = useMemo(() => {
    const selected = generateOptions(multi).filter(o => selectedAvailabilityCodeIds.includes(o.value));
    return multi ? selected : selected[0];
  }, [selectedAvailabilityCodeIds, multi, generateOptions]);

  // we shouldn't overwrite the param selectedAvailabilityCodes, so we generate
  // a new one based on the internal state
  const selectedAvailabilityCodesInternal = useMemo(
    () => selectedAvailabilityCodeIds.map(id => availabilityCodes.find(ac => ac.id === id)),
    [availabilityCodes, selectedAvailabilityCodeIds]
  );

  const applyToExistingIntervalsOnly =
    !multi && selectedAvailabilityCodesInternal.some(ac => ac.apply_to_existing_intervals_only);

  const synchronizeInRegion = !multi && selectedAvailabilityCodesInternal.some(ac => ac.synchronize_in_region);

  // This component will be rendered (for now) as part of a normal HTML form. To
  // make sure this works, we create a hidden normal input field with a value
  // equal to the currently selected availability code
  if (loading) return <BarLoader height="4px" width="100%" color="#C30000" />;

  return (
    <>
      {isEmpty(selectedAvailabilityCodeIds) && (
        // Render a hidden input with empty value to allow deleting all the
        // availability codes when multi select is enabled
        <input type="hidden" name={inputName} value="" />
      )}
      {selectedAvailabilityCodeIds.map(id => (
        <input key={id} type="hidden" name={inputName} value={id} />
      ))}
      <Select
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isMulti={multi}
        isClearable={true}
        isSearchable={true}
        className="react-select availability-codes"
        placeholder={I18n.t('none')}
        filterOption={filterOption}
        menuPosition="fixed"
        menuPortalTarget={document.body}
        styles={customStyles}
      />
      {applyToExistingIntervalsOnly && !hideExistingIntervalsWarning && (
        <p className="my-1 small">
          <IconCircleInfo />{' '}
          {I18n.t('activerecord.attributes.schedule/availability_code.apply_to_existing_intervals_only')}
        </p>
      )}
      {synchronizeInRegion && (
        <p className="my-1 small">
          <IconCircleInfo /> {I18n.t('activerecord.attributes.schedule/availability_code.synchronize_in_region')}
        </p>
      )}
      {customInfoComponent || null}
    </>
  );
};

export default AvailabilityCodeSelector;
